import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Send, CheckCircle, X } from "lucide-react";
import { submitAvis, getPublicLogements } from "@/lib/api";

export default function AvisForm({ onClose }) {
  const [step, setStep] = useState(1); // 1=infos, 2=avis, 3=success
  const [logements, setLogements] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    nom: "", email: "", type_temoignage: "voyageur",
    logement_id: "", logement_nom: "", note: 5, texte: "",
  });

  useEffect(() => {
    getPublicLogements().then(r => setLogements(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitAvis({
        nom: form.nom,
        email: form.email,
        type_temoignage: form.type_temoignage,
        logement_id: form.logement_id || undefined,
        logement_nom: form.logement_nom || undefined,
        note: form.note,
        texte: form.texte,
      });
      setStep(3);
    } catch (err) {
      alert(err?.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleLogementChange = (id) => {
    const log = logements.find(l => l.id === id);
    set("logement_id", id);
    set("logement_nom", log?.titre || "");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-[#FFFDF9] w-full max-w-md rounded-2xl shadow-2xl border border-[#E0D6CA] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-[#004333]">
          <div>
            <h3 className="text-white font-semibold">Laisser un avis</h3>
            <p className="text-white/50 text-xs mt-0.5">Votre avis sera publié après validation</p>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-1.5 text-white/50 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <AnimatePresence mode="wait">

          {/* Step 3 — Success */}
          {step === 3 && (
            <motion.div key="success" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h4 className="font-display text-xl text-[#004333] mb-2">Merci pour votre avis !</h4>
              <p className="text-[#6B5E50] text-sm leading-relaxed mb-6">
                Votre témoignage a bien été reçu. Il sera publié sur le site après validation par notre équipe.
              </p>
              {onClose && (
                <button onClick={onClose} className="btn-primary px-8">Fermer</button>
              )}
            </motion.div>
          )}

          {/* Form */}
          {step !== 3 && (
            <motion.form key="form" onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">

              {/* Qui êtes-vous */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Prénom Nom <span className="text-red-500">*</span>
                  </label>
                  <input type="text" required value={form.nom}
                    onChange={e => set("nom", e.target.value)}
                    placeholder="Marie D." className="input-hh text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input type="email" required value={form.email}
                    onChange={e => set("email", e.target.value)}
                    placeholder="marie@email.fr" className="input-hh text-sm" />
                </div>
              </div>

              {/* Vous êtes */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Vous êtes</label>
                <div className="flex gap-2">
                  {[
                    { value: "voyageur", label: "🧳 Voyageur" },
                    { value: "proprietaire", label: "🏠 Propriétaire" },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => set("type_temoignage", opt.value)}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                        form.type_temoignage === opt.value
                          ? "bg-[#004333] text-white border-[#004333]"
                          : "bg-white text-gray-500 border-gray-200 hover:border-[#004333]/30"
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Logement concerné */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Logement concerné <span className="text-gray-400 font-normal normal-case">(optionnel)</span>
                </label>
                {logements.length > 0 ? (
                  <select value={form.logement_id} onChange={e => handleLogementChange(e.target.value)}
                    className="input-hh text-sm">
                    <option value="">— Aucun logement spécifique —</option>
                    {logements.map(l => (
                      <option key={l.id} value={l.id}>{l.titre} — {l.ville}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={form.logement_nom}
                    onChange={e => set("logement_nom", e.target.value)}
                    placeholder="Nom du logement" className="input-hh text-sm" />
                )}
              </div>

              {/* Note */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Note</label>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" onClick={() => set("note", n)} className="p-1 transition-transform hover:scale-110">
                      <Star className={`w-7 h-7 transition-colors ${n <= form.note ? "fill-[#C9922A] text-[#C9922A]" : "text-gray-200"}`} />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-[#6B5E50] self-center font-medium">{form.note}/5</span>
                </div>
              </div>

              {/* Avis */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Votre avis <span className="text-red-500">*</span>
                </label>
                <textarea required rows={8} value={form.texte}
                  onChange={e => set("texte", e.target.value)}
                  placeholder="Partagez votre expérience avec Happy House..."
                  className="input-hh text-sm resize-none" minLength={20} />
                <p className="mt-1 text-xs text-gray-400">{form.texte.length} caractères (minimum 20)</p>
              </div>

              <p className="text-xs text-[#8B7D6B] bg-[#F5F0EB] rounded-xl p-3 leading-relaxed">
                🔒 Votre email ne sera pas publié. Votre avis sera vérifié par notre équipe avant d'apparaître sur le site.
              </p>

              <button type="submit" disabled={submitting || form.texte.length < 20}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-[#C9922A] to-[#E8C874] text-[#1A120B] font-bold rounded-full hover:shadow-[0_4px_20px_rgba(201,146,42,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? (
                  <span className="w-4 h-4 border-2 border-[#1A120B]/30 border-t-[#1A120B] rounded-full animate-spin" />
                ) : <Send className="w-4 h-4" />}
                {submitting ? "Envoi en cours..." : "Soumettre mon avis"}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
